import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, FileUp, Layers, Send, Handshake, FileCheck, Lock, Eye, Network, Activity } from 'lucide-react';
import { useReveal } from '@/lib/useReveal';

/**
 * Pathways — two-audience pathway summary (borrower / capital partner).
 * Conforms to GP CAP Public Site Design System v1: navy surface, single
 * card system, pill tabs, one primary CTA per pane, single signature
 * scroll-reveal on enter.
 */

const BORROWER_STEPS = [
  { icon: FileUp,    title: 'Submit opportunity',      body: 'Send the opportunity through a confidential intake. We acknowledge within one business day.' },
  { icon: Layers,    title: 'Package documents',       body: 'IM, deal points and supporting documents structured into a clear capital-partner-ready pack.' },
  { icon: Send,      title: 'Controlled distribution', body: 'Selective circulation to a matched, pre-qualified counterparty group under NDA.' },
  { icon: Handshake, title: 'Negotiate terms',         body: 'Term sheets and revisions managed through a single workflow with clear audit history.' },
  { icon: FileCheck, title: 'Execute',                 body: 'Selected counterparty progresses to documentation, settlement and close.' },
];

const LENDER_STEPS = [
  { icon: Eye,       title: 'Curated opportunities',    body: 'Opportunities filtered to your mandate, geography and ticket envelope — never broadcast.' },
  { icon: Lock,      title: 'NDA-gated data room',      body: 'Role-based access with controlled document preview and watermarked downloads where applicable.' },
  { icon: Activity,  title: 'Structured response',      body: 'Indicative interest, term sheets and Q&A captured in-platform for clean comparability.' },
  { icon: Network,   title: 'Mandate-fit matching',     body: 'Repeat scoring across deals improves the relevance of every subsequent introduction.' },
  { icon: Handshake, title: 'Relationship intelligence', body: 'Same counterparty profile retained across mandates — no re-onboarding on every deal.' },
];

const Tab = ({ active, onClick, testId, children }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    data-testid={testId}
    className="px-4 py-2 rounded-full text-sm font-medium"
    style={{
      background: active ? 'var(--gpc-gold)' : 'transparent',
      color: active ? 'var(--gpc-paper-2)' : 'var(--gpc-ink)',
      border: '1px solid ' + (active ? 'var(--gpc-gold)' : 'var(--gpc-line-strong)'),
      fontFamily: 'Inter, system-ui, sans-serif',
      transition: 'transform 0.2s ease, background-color 0.2s ease, border-color 0.2s ease',
    }}
  >
    {children}
  </button>
);

const StepCard = ({ step, index }) => (
  <div
    className="gpc-card flex items-start gap-4"
    data-testid={`pathway-step-${index + 1}`}
  >
    <div
      className="w-9 h-9 rounded-md flex items-center justify-center shrink-0 mt-0.5"
      style={{ background: 'rgba(201,168,106,0.10)' }}
    >
      <step.icon className="w-4 h-4" style={{ color: 'var(--gpc-gold)' }} />
    </div>
    <div className="min-w-0">
      <div className="flex items-baseline gap-2 mb-1">
        <span
          className="gpc-num text-[10px]"
          style={{ color: 'var(--gpc-muted)', letterSpacing: '0.2em' }}
        >
          {String(index + 1).padStart(2, '0')}
        </span>
        <h3 className="gpc-h3" style={{ fontSize: '15px' }}>{step.title}</h3>
      </div>
      <p className="gpc-body" style={{ fontSize: '14px', lineHeight: 1.6 }}>{step.body}</p>
    </div>
  </div>
);

export const Pathways = () => {
  const [tab, setTab] = useState('borrower');
  const [ref, visible] = useReveal({ threshold: 0.1 });

  const steps = tab === 'borrower' ? BORROWER_STEPS : LENDER_STEPS;
  const linkTo = tab === 'borrower' ? '/for-borrowers' : '/for-lenders';
  const linkLabel = tab === 'borrower' ? 'Full borrower pathway' : 'Full capital-partner pathway';

  return (
    <section
      ref={ref}
      className="gpc-section gpc-section--alt"
      data-testid="pathways-section"
    >
      <div className={`gpc-container gpc-reveal ${visible ? 'is-visible' : ''}`}>
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
          <div className="max-w-2xl">
            <p className="gpc-eyebrow mb-4">Private Capital · Borrowers &amp; Capital Partners</p>
            <h2 className="gpc-h2 mb-3">
              Two sides of the same <span style={{ color: 'var(--gpc-gold)', fontStyle: 'italic' }}>platform.</span>
            </h2>
            <p className="gpc-body max-w-xl">
              Borrowers and capital partners run through the same controlled workflow — only the entry point differs.
            </p>
          </div>
          <div className="flex gap-2" data-testid="pathways-tabs">
            <Tab active={tab === 'borrower'} onClick={() => setTab('borrower')} testId="pathway-tab-borrower">For Borrowers</Tab>
            <Tab active={tab === 'lender'} onClick={() => setTab('lender')} testId="pathway-tab-lender">For Capital Partners</Tab>
          </div>
        </div>

        <div
          key={tab /* remount keeps the per-section reveal coherent */}
          className="grid md:grid-cols-2 gap-3"
          data-testid={`pathway-steps-${tab}`}
        >
          {steps.map((s, i) => <StepCard key={`${tab}-${i}`} step={s} index={i} />)}
        </div>

        <div className="mt-10 flex flex-wrap items-center gap-5">
          <Link to={linkTo} className="gpc-cta-primary" data-testid="pathway-full-cta">
            {linkLabel}
            <ArrowRight className="w-4 h-4" />
          </Link>
          <div
            className="flex flex-wrap gap-x-5 gap-y-1"
            style={{
              fontFamily: 'Space Grotesk, system-ui, sans-serif',
              fontSize: '11px',
              letterSpacing: '0.06em',
              color: 'var(--gpc-muted)',
            }}
          >
            <span className="inline-flex items-center gap-1.5">
              <Lock className="w-3 h-3" style={{ color: 'var(--gpc-gold)' }} /> NDA-gated access
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Eye className="w-3 h-3" style={{ color: 'var(--gpc-gold)' }} /> Controlled document preview
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Activity className="w-3 h-3" style={{ color: 'var(--gpc-gold)' }} /> Audit-aware workflow
            </span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Pathways;
